import React from "react";
import FormRender from "components/form-builder/FormRender";
import { useSubscribeSocket } from "hooks/useSocket";
import { userApi } from "services/api";
import toast from "react-hot-toast";
import { showMutationError } from "utils/common";
import { formatSurveyResponse } from "utils/surveyUtils";
import { generateFileLookUp, uploadMediaFiles } from "utils/uploadUtils";
import { useMutation } from "@tanstack/react-query";
import { CACHE_KEYS } from "constants/common";
import queryClient from "config/queryClient";

function transformPayload(defaultValues) {
  const formValues = {};
  Object.entries(defaultValues).forEach(([key, value]) => {
    formValues[key] = value?.answer;
  });
  return formValues;
}

const QuestionerGuestSurveyPage = ({
  questioner,
  progress = {},
  user,
  handleChangeStep,
  defaultValues = {},
}) => {
  useSubscribeSocket();
  const userType = user.userType ?? user.usertype;
  const formSchema = questioner?.formJson;

  const { mutateAsync: saveSurvey } = useMutation({
    mutationFn: userApi.saveGuestUserSurvey,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.QUESTIONER_ANSWER],
      });
    },
  });

  const handleSubmit = async (payload, progress = {}) => {
    const id = toast.loading("Submitting Survey...");
    try {
      const { fileLookUp, files } = generateFileLookUp(payload);

      if (files?.length) {
        const uploadResponse = await uploadMediaFiles({
          fileLookUp,
          files,
          foldername: `survey-files/${userType}/${questioner.title}`,
          toastId: id,
        });

        Object.entries(uploadResponse).forEach(([key, value]) => {
          payload[key] = value;
        });
      }
      toast.loading("Submitting answers...", {
        id,
      });

      //UPLOAD ANY FILE AND GET FILE INFO HERE AND THEN SUBMIT FINAL FORM
      const result = formatSurveyResponse(formSchema, payload);

      await saveSurvey({
        id: questioner.id,
        data: {
          userResponse: result.formattedResponse,
          userId: user.id,
          progress: {
            stepCount: result.stepCount,
            fieldCount: result.fieldCount,
            ...progress,
          },
        },
      });

      toast.success("Response saved successfully");

      if (progress.isFinalStep) {
        handleChangeStep();
      }
    } catch (error) {
      showMutationError(error);
    } finally {
      toast.dismiss(id);
    }
  };

  return (
    <>
      <FormRender
        userType={userType}
        formSchema={formSchema}
        formSubmitHandler={handleSubmit}
        handleNextStep={handleSubmit}
        stepCount={progress?.stepCount ?? 0}
        key={`${JSON.stringify(progress)}`}
        defaultValues={transformPayload(defaultValues)}
      />
    </>
  );
};

export default QuestionerGuestSurveyPage;
